import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Loader2, ShieldCheck, X } from "lucide-react";

/**
 * Phase 17a — OAuth 2.0 consent screen.
 *
 * Renders after the caller has authenticated. Queries GET /oauth/authorize
 * with the full set of query params from the partner app. Renders scope
 * descriptions and Allow/Deny buttons. On Allow we POST /oauth/consent
 * and follow the returned `redirect` URL back to the partner app.
 */
export default function ConsentPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [state, setState] = useState({ loading: true, error: null, payload: null });
  const [submitting, setSubmitting] = useState(false);

  const requiredKeys = [
    "response_type", "client_id", "redirect_uri", "scope", "state",
    "code_challenge", "code_challenge_method",
  ];

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const query = {};
        for (const k of requiredKeys) {
          if (params.get(k) !== null) query[k] = params.get(k);
        }
        const { data } = await api.get("/oauth/authorize", { params: query });
        if (!cancelled) setState({ loading: false, error: null, payload: data });
      } catch (e) {
        if (cancelled) return;
        if (e?.response?.status === 401) {
          const login = e?.response?.data?.login_url;
          if (login) {
            navigate(login);
            return;
          }
        }
        setState({ loading: false, error: formatApiError(e), payload: null });
      }
    };
    load();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  const decide = async (decision) => {
    if (!state.payload) return;
    setSubmitting(true);
    try {
      const body = {
        client_id: state.payload.client_id,
        redirect_uri: state.payload.redirect_uri,
        scope: (state.payload.scope || []).join(" "),
        state: state.payload.state || "",
        code_challenge: state.payload.code_challenge,
        code_challenge_method: state.payload.code_challenge_method || "S256",
        decision,
      };
      const { data } = await api.post("/oauth/consent", body);
      if (data?.redirect) {
        window.location.replace(data.redirect);
      } else {
        toast.error("Consent flow error: no redirect returned");
      }
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  if (state.loading) {
    return (
      <div
        className="flex min-h-screen items-center justify-center bg-white"
        data-testid="consent-loading"
      >
        <div className="flex items-center gap-2 text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading consent&hellip;
        </div>
      </div>
    );
  }

  if (state.error) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-white" data-testid="consent-error">
        <Card className="max-w-md">
          <CardHeader>
            <CardTitle className="text-red-600">Cannot authorize</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">{state.error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const p = state.payload;
  return (
    <div
      className="flex min-h-screen items-center justify-center bg-slate-50 px-4"
      data-testid="consent-page"
    >
      <Card className="w-full max-w-lg">
        <CardHeader>
          <div className="flex items-center gap-2">
            <ShieldCheck className="h-5 w-5" style={{ color: "var(--quanta-navy)" }} />
            <div className="text-xs font-medium uppercase tracking-widest text-muted-foreground">
              Sign in with Quanta
            </div>
          </div>
          <CardTitle className="mt-2 text-2xl">
            <span data-testid="consent-app-name">{p.client_name}</span> wants to access your Quanta account
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <div className="mb-2 text-sm font-semibold">This will let the app:</div>
            <ul className="space-y-2" data-testid="consent-scope-list">
              {(p.scope_labels || []).map((s) => (
                <li
                  key={s.scope}
                  className="flex items-start gap-2 rounded-md border bg-white p-2 text-sm"
                  data-testid={`consent-scope-${s.scope}`}
                >
                  <Badge variant="outline" className="font-mono text-[10px]">{s.scope}</Badge>
                  <span className="text-muted-foreground">{s.description}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="text-xs text-muted-foreground">
            Redirecting to <code className="rounded bg-slate-100 px-1 py-0.5">{p.redirect_uri}</code>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button
              variant="outline"
              onClick={() => decide("deny")}
              disabled={submitting}
              data-testid="btn-consent-deny"
            >
              <X className="mr-2 h-4 w-4" /> Deny
            </Button>
            <Button
              onClick={() => decide("allow")}
              disabled={submitting}
              data-testid="btn-consent-allow"
            >
              {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Allow
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
